/**
 * Account Status Service
 * Handles comprehensive account status checking and access control
 * Integrates with Stripe billing and company management
 */

import Parse from 'parse'
import { parseCloudRun } from './parseCloud'
import { parseUserId } from '../utils/parseUserId'

export interface AccountStatus {
  isGlobalAdmin: boolean
  hasFullAccess: boolean
  subscriptionStatus: 'none' | 'active' | 'trialing' | 'past_due' | 'canceled' | 'cancelled' | 'suspended' | 'free_tier_active' | 'free_tier_exceeded'
  paymentStatus: 'none' | 'paid' | 'overdue' | 'failed' | 'suspended'
  canCreateRooms: boolean
  canJoinRooms: boolean
  canJoinCustomRooms?: boolean
  canAccessDashboard: boolean
  canAccessCompanyManagement: boolean
  canAccessBilling?: boolean
  canAccessAllFeatures: boolean
  restrictions: string[]
  company: {
    id: string
    name: string
    isActive: boolean
    billingTier?: string
    maxUsers?: number
    maxRooms?: number
  } | null
  userRole: string | null
  effectiveTier?: {
    id: string
    name: string
    tier_key: string
    description?: string
    base_price?: number
    price_per_user?: number
    free_users_limit?: number
    max_users?: number
    max_rooms?: number
    features?: Record<string, any>
    is_active?: boolean
    color?: string
  } | null
  tierSource?: 'company' | 'personal'
  personalTier?: {
    id: string
    name: string
    tierKey: string
    description?: string
  } | null
  billingAccount?: {
    id: string
    stripeCustomerId?: string
    stripeSubscriptionId?: string
    billingTierId?: string
    status?: string
    paymentStatus?: string
    billingCycle?: string
    lastPaymentDate?: Date
    nextBillingDate?: Date
    outstandingBalance?: number
  } | null
}

export interface ActionPermissionResult {
  allowed: boolean
  reason: string
  accountStatus: {
    subscriptionStatus: string
    paymentStatus: string
    restrictions: string[]
    company: any
    userRole: string
  }
}

function getCachedActionPermission(
  accountStatus: AccountStatus,
  action: string,
  _resource?: string
): ActionPermissionResult | null {
  let allowed = false
  let reason = ''

  if (accountStatus.isGlobalAdmin) {
    return {
      allowed: true,
      reason: 'Global admin access',
      accountStatus: {
        subscriptionStatus: accountStatus.subscriptionStatus,
        paymentStatus: accountStatus.paymentStatus,
        restrictions: accountStatus.restrictions,
        company: accountStatus.company,
        userRole: accountStatus.userRole,
      },
    }
  }

  switch (action) {
    case 'create_room':
      allowed = accountStatus.canCreateRooms
      reason = allowed
        ? 'Room creation allowed'
        : 'Room creation restricted - ' + (accountStatus.restrictions[0] || 'No active subscription')
      break
    case 'join_room':
      allowed = accountStatus.canJoinRooms
      reason = allowed
        ? 'Room joining allowed'
        : 'Room joining restricted - ' + (accountStatus.restrictions[0] || 'No active subscription')
      break
    case 'join_custom_room':
      allowed =
        accountStatus.canJoinCustomRooms === true ||
        accountStatus.subscriptionStatus === 'active' ||
        accountStatus.subscriptionStatus === 'trialing'
      reason = allowed
        ? 'Custom room joining allowed'
        : 'Custom room joining restricted - subscription or permission required'
      break
    case 'access_dashboard':
      allowed = accountStatus.canAccessDashboard
      reason = allowed ? 'Dashboard access allowed' : 'Dashboard access restricted'
      break
    case 'access_company_management':
      allowed = accountStatus.canAccessCompanyManagement
      reason = allowed ? 'Company management access allowed' : 'Company management access restricted'
      break
    case 'access_billing':
      // Same rule as the cloud checkActionPermission, so the guard decides identically whether
      // it asks the cloud or the cached status. The cloud answers from `canAccessBilling` (the
      // company permission `billing.view_plan`, SC-439) and falls back to the manager/admin role
      // only when the status carries no flag.
      allowed =
        accountStatus.company !== null &&
        accountStatus.company !== undefined &&
        (typeof accountStatus.canAccessBilling === 'boolean'
          ? accountStatus.canAccessBilling
          : accountStatus.userRole === 'manager' || accountStatus.userRole === 'admin')
      reason = allowed
        ? 'Billing access allowed'
        : !accountStatus.company
          ? 'No company association'
          : 'Billing access restricted - manager or admin role required'
      break
    case 'access_profile':
      allowed = true
      reason = 'Profile access always allowed'
      break
    default:
      return null
  }

  return {
    allowed,
    reason,
    accountStatus: {
      subscriptionStatus: accountStatus.subscriptionStatus,
      paymentStatus: accountStatus.paymentStatus,
      restrictions: accountStatus.restrictions,
      company: accountStatus.company,
      userRole: accountStatus.userRole,
    },
  }
}

export interface SubscriptionDetails {
  company: {
    id: string
    name: string
    isActive: boolean
    billingTier?: string
    maxUsers?: number
    maxRooms?: number
    trialEndsAt?: Date
    subscriptionEndsAt?: Date
  }
  billingAccount: {
    id: string
    stripeCustomerId?: string
    stripeSubscriptionId?: string
    billingTierId?: string
    status?: string
    paymentStatus?: string
    billingCycle?: string
    lastPaymentDate?: Date
    nextBillingDate?: Date
    lastPaymentAmount?: number
    totalPaid?: number
    outstandingBalance?: number
  } | null
  billingTier: {
    id: string
    name: string
    tierKey: string
    description?: string
    basePrice: number
    pricePerUser: number
    freeUsersLimit: number
    maxUsers: number
    maxRooms: number
    features: Record<string, any>
    isActive: boolean
  } | null
  billingHistory: Array<{
    id: string
    amount: number
    status: string
    description: string
    billingDate: Date
    stripeInvoiceId?: string
  }>
}

const currentUserId = () => parseUserId(Parse)

export class AccountStatusService {
  private static instance: AccountStatusService
  private accountStatusCache: AccountStatus | null = null
  private cacheTimestamp: number = 0
  // Owner of the cached status. The key is shared by every user of the browser and a sign-in that
  // does not log out first (become()) leaves it behind, so it must never be served to another user.
  private cacheUid: string | null = null
  private readonly CACHE_DURATION = 5 * 60 * 1000 // 5 minutes
  private readonly LS_KEY = 'wiggin_account_status'
  // One shared request for concurrent callers; a cold dashboard load fires 3-7 at once.
  private inFlight: Promise<AccountStatus> | null = null
  // Lets a superseded request (forced refresh, clearCache) finish without writing the cache.
  private requestSeq = 0

  public static getInstance(): AccountStatusService {
    if (!AccountStatusService.instance) {
      AccountStatusService.instance = new AccountStatusService()
    }
    return AccountStatusService.instance
  }

  /**
   * Try to hydrate from localStorage so page reloads show the UI instantly. An entry counts only
   * when its stamp equals the current user: a missing stamp (written before the stamp existed) or
   * an unknown current user is a miss, so the cloud decides.
   */
  private readLocalStorageEntry(): { value: AccountStatus; ts: number; uid: string } | null {
    try {
      const raw = localStorage.getItem(this.LS_KEY)
      if (!raw) return null
      const { value, ts, uid } = JSON.parse(raw)
      const currentUid = currentUserId()
      if (!uid || !currentUid || uid !== currentUid) return null
      if (Date.now() - ts < this.CACHE_DURATION) return { value: value as AccountStatus, ts, uid }
      return null
    } catch { return null }
  }

  /**
   * Last-resort status for a failed cloud call: any age in memory (as before), but only the
   * current user's.
   */
  private getOwnCachedStatusForFallback(): AccountStatus | null {
    const currentUid = currentUserId()
    if (this.accountStatusCache && this.cacheUid !== null && this.cacheUid === currentUid) {
      return this.accountStatusCache
    }
    return this.readLocalStorageEntry()?.value ?? null
  }

  /**
   * Cached status that is under CACHE_DURATION old, from memory or localStorage. The timestamp
   * keeps the real age of the stored entry, so a reload does not make an old status look new.
   */
  private getFreshCachedStatus(): AccountStatus | null {
    const currentUid = currentUserId()
    if (
      this.accountStatusCache &&
      this.isCacheValid() &&
      this.cacheUid !== null &&
      this.cacheUid === currentUid
    ) {
      return this.accountStatusCache
    }
    const stored = this.readLocalStorageEntry()
    if (!stored) return null
    this.accountStatusCache = stored.value
    this.cacheTimestamp = stored.ts
    this.cacheUid = stored.uid
    return stored.value
  }

  /**
   * Allow/deny decision for an action from the status cached less than CACHE_DURATION ago, or
   * null when there is none (or the action is not decided locally) and the caller must ask the
   * cloud. The rules mirror cloud checkActionPermission.
   */
  getFreshCachedActionPermission(action: string, resource?: string): ActionPermissionResult | null {
    const status = this.getFreshCachedStatus()
    return status ? getCachedActionPermission(status, action, resource) : null
  }

  private writeLocalStorageCache(status: AccountStatus, uid: string | null): void {
    // Without an owner the entry could never be read back, so don't store it.
    if (!uid) return
    try {
      localStorage.setItem(this.LS_KEY, JSON.stringify({ value: status, ts: Date.now(), uid }))
    } catch { /* quota errors */ }
  }

  /**
   * Get comprehensive account status for the current user
   */
  async getAccountStatus(forceRefresh: boolean = false): Promise<AccountStatus> {
    if (!forceRefresh) {
      // In-memory first; on page reload the memory cache is empty, so try localStorage
      const cached = this.getFreshCachedStatus()
      if (cached) return cached

      if (this.inFlight) return this.inFlight
    }

    // A forced refresh never joins an older request: it may predate the change being refreshed.
    const seq = ++this.requestSeq
    const request: Promise<AccountStatus> = this.fetchAccountStatus(seq).finally(() => {
      if (this.inFlight === request) this.inFlight = null
    })
    this.inFlight = request
    return request
  }

  private async fetchAccountStatus(seq: number): Promise<AccountStatus> {
    // Taken before the request: the answer belongs to whoever asked, even if the session changes
    // while it is in flight.
    const requesterUid = currentUserId()
    try {
      // Cap the cloud function call at 10 s so a slow Back4App / Stripe
      // round-trip never blocks the dashboard indefinitely.
      const timeout = new Promise<never>((_, reject) =>
        setTimeout(() => reject(new Error('getAccountStatus timed out')), 10000)
      )
      const result = await Promise.race([
        parseCloudRun<{ success: boolean; accountStatus: AccountStatus }>('getAccountStatus'),
        timeout,
      ])

      if (result.success) {
        if (seq === this.requestSeq) {
          this.accountStatusCache = result.accountStatus
          this.cacheTimestamp = Date.now()
          this.cacheUid = requesterUid
          this.writeLocalStorageCache(result.accountStatus, requesterUid)
        }
        return result.accountStatus
      } else {
        throw new Error('Failed to get account status')
      }
    } catch (error) {
      console.error('Error getting account status:', error)
      // Return default restricted status on error
      return {
        isGlobalAdmin: false,
        hasFullAccess: false,
        subscriptionStatus: 'none',
        paymentStatus: 'none',
        canCreateRooms: false,
        canJoinRooms: false,
        canJoinCustomRooms: false,
        canAccessDashboard: false,
        canAccessCompanyManagement: false,
        canAccessBilling: false,
        canAccessAllFeatures: false,
        restrictions: ['Failed to check account status'],
        company: null,
        userRole: null
      }
    }
  }

  /**
   * Check if user can perform a specific action
   */
  async checkActionPermission(action: string, resource?: string): Promise<ActionPermissionResult> {
    try {
      // Cap at 8 s so a slow connection doesn't block UI gating on this call.
      const timeout = new Promise<never>((_, reject) =>
        setTimeout(() => reject(new Error('checkActionPermission timed out')), 8000)
      )
      const result = await Promise.race([
        parseCloudRun<{
          success: boolean
          allowed: boolean
          reason?: string
          accountStatus: ActionPermissionResult['accountStatus']
        }>('checkActionPermission', { action, resource }),
        timeout,
      ])

      if (result.success) {
        const isTransientAnonymousResult =
          result.allowed === false &&
          result.accountStatus?.userRole === 'anonymous' &&
          Array.isArray(result.accountStatus?.restrictions) &&
          result.accountStatus.restrictions.includes('User not authenticated')

        if (isTransientAnonymousResult) {
          const cachedStatus = this.getOwnCachedStatusForFallback()
          const cachedResult = cachedStatus ? getCachedActionPermission(cachedStatus, action, resource) : null
          if (cachedResult) {
            console.warn('[AccountStatusService] Using cached account status while session is restoring', {
              action,
              resource,
            })
            // Do not restamp the cache here: that would make a stale status look fresh to
            // getFreshCachedActionPermission.
            return cachedResult
          }
        }

        return {
          allowed: result.allowed,
          reason: result.reason,
          accountStatus: result.accountStatus
        }
      } else {
        throw new Error('Failed to check action permission')
      }
    } catch (error) {
      console.error('Error checking action permission:', error)

      const cachedStatus = this.getOwnCachedStatusForFallback()
      const cachedResult = cachedStatus ? getCachedActionPermission(cachedStatus, action, resource) : null
      if (cachedResult) {
        console.warn('[AccountStatusService] Falling back to cached account status for permission check', {
          action,
          resource,
        })
        return cachedResult
      }

      return {
        allowed: false,
        reason: 'Failed to check permissions',
        accountStatus: {
          subscriptionStatus: 'none',
          paymentStatus: 'none',
          restrictions: ['Permission check failed'],
          company: null,
          userRole: null
        }
      }
    }
  }

  /**
   * Get subscription details for a company
   */
  async getCompanySubscriptionDetails(companyId: string): Promise<SubscriptionDetails> {
    try {
      const result = await Parse.Cloud.run('getCompanySubscriptionDetails', {
        company_id: companyId
      })

      if (result.success) {
        return result.subscriptionDetails
      } else {
        throw new Error('Failed to get subscription details')
      }
    } catch (error) {
      console.error('Error getting subscription details:', error)
      throw error
    }
  }

  /**
   * Check if user can create rooms
   */
  async canCreateRoom(): Promise<boolean> {
    try {
      const result = await this.checkActionPermission('create_room')
      return result.allowed
    } catch (error) {
      console.error('Error checking room creation permission:', error)
      return false
    }
  }

  /**
   * Check if user can join rooms (global/default rooms)
   */
  async canJoinRoom(): Promise<boolean> {
    try {
      const result = await this.checkActionPermission('join_room')
      return result.allowed
    } catch (error) {
      console.error('Error checking room joining permission:', error)
      return false
    }
  }

  /**
   * Check if user can join custom rooms (requires active subscription)
   */
  async canJoinCustomRoom(): Promise<boolean> {
    try {
      const result = await this.checkActionPermission('join_custom_room')
      return result.allowed
    } catch (error) {
      console.error('Error checking custom room joining permission:', error)
      return false
    }
  }

  /**
   * Check if user can access dashboard
   */
  async canAccessDashboard(): Promise<boolean> {
    try {
      const result = await this.checkActionPermission('access_dashboard')
      return result.allowed
    } catch (error) {
      console.error('Error checking dashboard access permission:', error)
      return false
    }
  }

  /**
   * Check if user can access company management
   */
  async canAccessCompanyManagement(): Promise<boolean> {
    try {
      const result = await this.checkActionPermission('access_company_management')
      return result.allowed
    } catch (error) {
      console.error('Error checking company management access permission:', error)
      return false
    }
  }

  /**
   * Check if user can access billing (requires manager/admin role)
   */
  async canAccessBilling(): Promise<boolean> {
    try {
      const result = await this.checkActionPermission('access_billing')
      return result.allowed
    } catch (error) {
      console.error('Error checking billing access permission:', error)
      return false
    }
  }

  /**
   * Check if user can access profile (always allowed)
   */
  async canAccessProfile(): Promise<boolean> {
    try {
      const result = await this.checkActionPermission('access_profile')
      return result.allowed
    } catch (error) {
      console.error('Error checking profile access permission:', error)
      return true // Profile access is always allowed
    }
  }

  /**
   * Get detailed permission check with reason
   */
  async getDetailedPermissionCheck(action: string): Promise<{
    allowed: boolean
    reason: string
    restrictions: string[]
    subscriptionStatus: string
    paymentStatus: string
  }> {
    try {
      const result = await this.checkActionPermission(action)
      return {
        allowed: result.allowed,
        reason: result.reason,
        restrictions: result.accountStatus.restrictions,
        subscriptionStatus: result.accountStatus.subscriptionStatus,
        paymentStatus: result.accountStatus.paymentStatus
      }
    } catch (error) {
      console.error('Error getting detailed permission check:', error)
      return {
        allowed: false,
        reason: 'Failed to check permissions',
        restrictions: ['Permission check failed'],
        subscriptionStatus: 'none',
        paymentStatus: 'none'
      }
    }
  }

  /**
   * Check if account has any restrictions
   */
  async hasRestrictions(): Promise<boolean> {
    try {
      const accountStatus = await this.getAccountStatus()
      return accountStatus.restrictions.length > 0
    } catch (error) {
      console.error('Error checking restrictions:', error)
      return true // Assume restricted on error
    }
  }

  /**
   * Get all current restrictions
   */
  async getRestrictions(): Promise<string[]> {
    try {
      const accountStatus = await this.getAccountStatus()
      return accountStatus.restrictions
    } catch (error) {
      console.error('Error getting restrictions:', error)
      return ['Failed to check account status']
    }
  }

  /**
   * Check if user is a global admin
   */
  async isGlobalAdmin(): Promise<boolean> {
    try {
      const accountStatus = await this.getAccountStatus()
      return accountStatus.isGlobalAdmin
    } catch (error) {
      console.error('Error checking global admin status:', error)
      return false
    }
  }

  /**
   * Check if user has full access to all features
   */
  async hasFullAccess(): Promise<boolean> {
    try {
      const accountStatus = await this.getAccountStatus()
      return accountStatus.hasFullAccess
    } catch (error) {
      console.error('Error checking full access status:', error)
      return false
    }
  }

  /**
   * Get subscription status
   */
  async getSubscriptionStatus(): Promise<string> {
    try {
      const accountStatus = await this.getAccountStatus()
      return accountStatus.subscriptionStatus
    } catch (error) {
      console.error('Error getting subscription status:', error)
      return 'none'
    }
  }

  /**
   * Get payment status
   */
  async getPaymentStatus(): Promise<string> {
    try {
      const accountStatus = await this.getAccountStatus()
      return accountStatus.paymentStatus
    } catch (error) {
      console.error('Error getting payment status:', error)
      return 'none'
    }
  }

  /**
   * Clear cache (useful after subscription changes)
   */
  clearCache(): void {
    this.accountStatusCache = null
    this.cacheTimestamp = 0
    this.cacheUid = null
    // A request started before sign-out/sign-in or a plan change must not repopulate the cache
    // or be handed to later callers.
    this.inFlight = null
    this.requestSeq++
    try {
      localStorage.removeItem(this.LS_KEY)
    } catch {
      /* ignore */
    }
  }

  /**
   * Check if cache is still valid
   */
  private isCacheValid(): boolean {
    return Date.now() - this.cacheTimestamp < this.CACHE_DURATION
  }

  /**
   * Force refresh account status
   */
  async refreshAccountStatus(): Promise<AccountStatus> {
    this.clearCache()
    return await this.getAccountStatus(true)
  }

  /**
   * Get user-friendly status message
   */
  async getStatusMessage(): Promise<string> {
    try {
      const accountStatus = await this.getAccountStatus()

      if (accountStatus.isGlobalAdmin) {
        return 'Global Administrator - Full Access'
      }

      if (accountStatus.hasFullAccess) {
        return 'Active Subscription - Full Access'
      }

      if (accountStatus.restrictions.length > 0) {
        return accountStatus.restrictions[0]
      }

      return 'Limited Access'
    } catch (error) {
      console.error('Error getting status message:', error)
      return 'Status Unknown'
    }
  }

  /**
   * Get tier restriction message for a specific feature
   */
  async getTierRestrictionMessage(feature: string): Promise<{
    title: string
    message: string
    actionText: string
    actionUrl: string
    featureName: string
  } | null> {
    const accountStatus = await this.getAccountStatus()

    if (!accountStatus) return null

    const subscriptionStatus = accountStatus.subscriptionStatus
    const paymentStatus = accountStatus.paymentStatus

    // Payment issues take precedence
    if (paymentStatus === 'overdue') {
      return {
        title: 'Payment Overdue',
        message: 'Your payment is overdue. Please update your payment method to continue using this feature.',
        actionText: 'Update Payment',
        actionUrl: '/company/billing',
        featureName: feature.replace('_', ' ')
      }
    }

    if (paymentStatus === 'failed') {
      return {
        title: 'Payment Failed',
        message: 'Your payment failed. Please update your payment method to continue using this feature.',
        actionText: 'Update Payment',
        actionUrl: '/company/billing',
        featureName: feature.replace('_', ' ')
      }
    }

    // Subscription status messages
    switch (subscriptionStatus) {
      case 'free_tier_exceeded':
        return {
          title: 'Free Tier Exceeded',
          message: `Your company has exceeded the free plan limits. Please upgrade to continue using ${feature.replace('_', ' ')}.`,
          actionText: 'Upgrade Plan',
          actionUrl: '/company/billing',
          featureName: feature.replace('_', ' ')
        }

      case 'none':
        return {
          title: 'No Active Subscription',
          message: `This feature requires an active subscription. Please subscribe to access ${feature.replace('_', ' ')}.`,
          actionText: 'Subscribe Now',
          actionUrl: '/company/billing',
          featureName: feature.replace('_', ' ')
        }

      case 'canceled':
      case 'cancelled':
        return {
          title: 'Subscription Canceled',
          message: `Your subscription has been canceled. Please renew to continue using ${feature.replace('_', ' ')}.`,
          actionText: 'Renew Subscription',
          actionUrl: '/company/billing',
          featureName: feature.replace('_', ' ')
        }

      case 'past_due':
        return {
          title: 'Subscription Past Due',
          message: `Your subscription is past due. Please update your payment method to continue using ${feature.replace('_', ' ')}.`,
          actionText: 'Update Payment',
          actionUrl: '/company/billing',
          featureName: feature.replace('_', ' ')
        }

      case 'suspended':
        return {
          title: 'Subscription Suspended',
          message: `Your subscription has been suspended. Please contact support to restore access to ${feature.replace('_', ' ')}.`,
          actionText: 'Contact Support',
          actionUrl: '/company/billing',
          featureName: feature.replace('_', ' ')
        }

      default:
        // For active/trialing subscriptions, check specific feature restrictions
        if (feature === 'create_room' && !accountStatus.canCreateRooms) {
          return {
            title: 'Room Creation Restricted',
            message: 'Your current plan does not include room creation. Please upgrade to create custom rooms.',
            actionText: 'Upgrade Plan',
            actionUrl: '/company/billing',
            featureName: 'Room Creation'
          }
        }

        if (feature === 'join_custom_room' && subscriptionStatus !== 'active' && subscriptionStatus !== 'trialing') {
          return {
            title: 'Custom Room Access Restricted',
            message: 'Joining custom rooms requires an active subscription. Please upgrade to access custom rooms.',
            actionText: 'Upgrade Plan',
            actionUrl: '/company/billing',
            featureName: 'Custom Room Access'
          }
        }

        return null
    }
  }

  /**
   * Check if user needs to take action (payment, subscription, etc.)
   * NOTE: Subscription checks are temporarily disabled
   */
  async needsAction(): Promise<{
    needsAction: boolean
    actionType: 'payment' | 'subscription' | 'none'
    message: string
  }> {
    try {
      // Use cached status (or fetch if not yet cached) — do NOT force-refresh here.
      // Previously this called getAccountStatus(true) which bypassed the cache and
      // issued a second uncached Parse.Cloud.run('getAccountStatus') every single
      // time useAccountStatus ran loadAccountStatus(), doubling network load and
      // causing the dashboard to hang on slow connections waiting for Stripe.
      const accountStatus = await this.getAccountStatus(false)

      if (accountStatus.isGlobalAdmin) {
        return {
          needsAction: false,
          actionType: 'none',
          message: 'No action needed - Global Admin'
        }
      }

      // If user has company access (canAccessCompanyManagement), they don't need action
      // This prevents false positives for users with company access but canceled personal subscriptions
      if (accountStatus.canAccessCompanyManagement) {
        return {
          needsAction: false,
          actionType: 'none',
          message: 'No action needed - Company access available'
        }
      }

      // TEMPORARILY DISABLED: Subscription checks to prevent false positives
      // TODO: Re-enable subscription checks once billing system is properly configured

      // Only check for critical payment issues (overdue payments)
      if (accountStatus.paymentStatus === 'overdue') {
        return {
          needsAction: true,
          actionType: 'payment',
          message: 'Payment overdue - please update payment method'
        }
      }

      // All subscription status checks are disabled for now
      // if (accountStatus.subscriptionStatus === 'canceled' || accountStatus.subscriptionStatus === 'cancelled') {
      //   return {
      //     needsAction: true,
      //     actionType: 'subscription',
      //     message: 'Subscription canceled - please renew to continue'
      //   }
      // }

      // if (accountStatus.subscriptionStatus === 'free_tier_exceeded') {
      //   return {
      //     needsAction: true,
      //     actionType: 'subscription',
      //     message: 'Free tier limit exceeded - please subscribe to continue'
      //   }
      // }

      // if (accountStatus.subscriptionStatus === 'none') {
      //   return {
      //     needsAction: true,
      //     actionType: 'subscription',
      //     message: 'No active subscription - please subscribe to continue'
      //   }
      // }

      return {
        needsAction: false,
        actionType: 'none',
        message: 'No action needed'
      }
    } catch (error) {
      console.error('Error checking if action is needed:', error)
      return {
        needsAction: false, // Changed to false to prevent blocking on errors
        actionType: 'none',
        message: 'No action needed - Error checking status'
      }
    }
  }
}

// Export singleton instance
export const accountStatusService = AccountStatusService.getInstance()
